import clsx from 'clsx'
import {
  Activity,
  BookOpen,
  Bot,
  Brain,
  CornerDownLeft,
  Database,
  IdCard,
  MessageSquare,
  Search,
  ShieldCheck,
  UserRound,
  UsersRound,
  X
} from 'lucide-react'
import { type KeyboardEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  groupPaletteItems,
  type PaletteGroupId,
  type PaletteItem,
  rankPaletteItems
} from '@/components/shell/command-palette-search.ts'
import { useFocusTrap } from '@/components/ui/focus-trap.ts'
import { IconButton, Spinner } from '@/components/ui/primitives.tsx'
import { useConversations } from '@/hooks/use-api.ts'
import { useGroups } from '@/hooks/use-groups.ts'
import { useUsableKnowledgeBases } from '@/hooks/use-knowledge-bases.ts'
import { useGallery } from '@/hooks/use-marketplace.ts'
import { useI18n } from '@/i18n/index.ts'

/**
 * Jump to anything already in the workspace.
 *
 * **This navigates and nothing else.** It searches only what the app can already list —
 * conversations, agents shared with this viewer, knowledge bases they can use, and groups
 * (whose names everyone may see, PRD-26 D-7) — and every result is a route that exists in the
 * router. There is no new endpoint behind it, no
 * server-side search, and no result that *does* something rather than going somewhere. The
 * palette is a keyboard shortcut to screens, not a new capability.
 *
 * Mounted only while it is open, which is load-bearing rather than tidiness: the focus trap
 * sets itself up once, on mount, so a version that stayed mounted and hid itself would leave
 * the trap wired to a container that did not exist when the effect ran. Mounting on open also
 * means its queries fire on first use and never on any other page.
 *
 * Not a `combobox`. A searchbox over a listbox is the simpler correct pattern here — there is
 * no inline completion and no value to commit, only a list to walk.
 */

const DESTINATION_ICONS: Record<string, ReactNode> = {
  '/': <MessageSquare size={13} aria-hidden />,
  '/agents': <Bot size={13} aria-hidden />,
  '/agents/mine': <UserRound size={13} aria-hidden />,
  '/knowledge': <BookOpen size={13} aria-hidden />,
  '/memory': <Brain size={13} aria-hidden />,
  '/groups': <UsersRound size={13} aria-hidden />,
  '/activity': <Activity size={13} aria-hidden />,
  '/governance': <ShieldCheck size={13} aria-hidden />,
  '/people': <IdCard size={13} aria-hidden />
}

/**
 * A glyph per rail, keyed structurally (PRD-27 F-9).
 *
 * `satisfies Record<PaletteGroupId, …>` is what makes a new rail a compile error here rather
 * than a magnifier on every one of its rows. `destinations` is deliberately `null` and not
 * missing: a destination is a route and wears its own glyph from `DESTINATION_ICONS`, so the
 * entry says "ask the route" rather than leaving a hole that reads as an oversight.
 */
const GROUP_ICONS = {
  conversations: <MessageSquare size={13} aria-hidden />,
  agents: <Bot size={13} aria-hidden />,
  knowledge: <Database size={13} aria-hidden />,
  groups: <UsersRound size={13} aria-hidden />,
  destinations: null
} satisfies Record<PaletteGroupId, ReactNode | null>

/**
 * A route this palette can jump to. A helper rather than an inline literal because the gated
 * ones are spread out of a conditional, where TypeScript loses the contextual `PaletteItem`
 * and would widen `groupId` back to `string` — the exact looseness F-9 was about.
 */
const destination = (id: string, title: string, to: string): PaletteItem => ({
  id,
  title,
  groupId: 'destinations',
  to
})

export const CommandPalette = ({
  onClose,
  canSeeActivity,
  canSeeGovernance,
  canSeePeople
}: {
  onClose: () => void
  canSeeActivity: boolean
  canSeeGovernance: boolean
  canSeePeople: boolean
}) => {
  // In the body, so a locale switch re-ranks and re-labels rather than freezing whatever
  // language this module was imported under (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.shell
  const navigate = useNavigate()
  const panel = useRef<HTMLDivElement>(null)
  const input = useRef<HTMLInputElement>(null)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)

  const conversations = useConversations()
  const gallery = useGallery()
  const bases = useUsableKnowledgeBases()
  const groupList = useGroups({ scope: 'all', limit: 200 })

  useFocusTrap(panel, { onEscape: onClose })

  // The trap focuses the container so a reader hears the dialog's name before its contents.
  // A palette is the one dialog where that is not enough, because its whole purpose is to be
  // typed into — and this effect runs after the trap's, so it wins.
  useEffect(() => {
    input.current?.focus()
  }, [])

  const items = useMemo<PaletteItem[]>(() => {
    const conversationItems: PaletteItem[] = (conversations.data?.items ?? [])
      .filter((conversation) => !conversation.archived)
      .map((conversation) => ({
        id: `c-${conversation.id}`,
        title: conversation.title,
        detail: conversation.lastMessagePreview ?? undefined,
        groupId: 'conversations',
        to: `/c/${conversation.id}`
      }))

    // The gallery's rails overlap by design — an agent can be featured *and* recently used —
    // so this dedupes by id. A repeated row makes the arrow keys feel broken.
    const seen = new Set<string>()
    const agentItems: PaletteItem[] = []
    for (const card of [
      ...(gallery.data?.mine ?? []),
      ...(gallery.data?.featured ?? []),
      ...(gallery.data?.sharedWithYourGroups ?? []),
      ...(gallery.data?.recent ?? []),
      ...(gallery.data?.all ?? [])
    ]) {
      if (seen.has(card.agentId)) continue
      seen.add(card.agentId)
      agentItems.push({
        id: `a-${card.agentId}`,
        title: card.displayName,
        detail: card.tagline || undefined,
        groupId: 'agents',
        to: `/agents/${card.agentId}`
      })
    }

    const baseItems: PaletteItem[] = (bases.data?.items ?? []).map((base) => ({
      id: `k-${base.id}`,
      title: base.name,
      detail: base.description || undefined,
      groupId: 'knowledge',
      to: `/knowledge/${encodeURIComponent(base.id)}`
    }))

    // Your own groups first — they are the ones you are likely looking for — then the rest.
    const groupItems: PaletteItem[] = [...(groupList.data?.items ?? [])]
      .sort((a, b) => Number(b.isMember || b.isManager) - Number(a.isMember || a.isManager))
      .map((group) => ({
        id: `g-${group.id}`,
        title: group.name,
        detail: fmt.t.groups.page.rowMeta(fmt, group.memberCount, group.isManager, group.isMember),
        groupId: 'groups',
        to: `/groups/${encodeURIComponent(group.id)}`
      }))

    const destinations: PaletteItem[] = [
      // Home is also the "start a conversation" answer, which is why the palette needs no verb
      // of its own for it: `/` is where a conversation is composed (PRD-19 NAV-002).
      {
        id: 'go-home',
        title: t.palette.home,
        detail: t.palette.homeDetail,
        groupId: 'destinations',
        to: '/'
      },
      { id: 'go-agents', title: t.nav.agents, groupId: 'destinations', to: '/agents' },
      { id: 'go-mine', title: t.palette.yourAgents, groupId: 'destinations', to: '/agents/mine' },
      { id: 'go-knowledge', title: t.nav.knowledge, groupId: 'destinations', to: '/knowledge' },
      { id: 'go-memory', title: t.nav.memory, groupId: 'destinations', to: '/memory' },
      { id: 'go-groups', title: t.nav.groups, groupId: 'destinations', to: '/groups' },
      ...(canSeeActivity ? [destination('go-activity', t.nav.activity, '/activity')] : []),
      ...(canSeeGovernance ? [destination('go-governance', t.nav.governance, '/governance')] : []),
      ...(canSeePeople ? [destination('go-people', t.nav.people, '/people')] : [])
    ]

    return [...conversationItems, ...agentItems, ...baseItems, ...groupItems, ...destinations]
  }, [
    conversations.data,
    gallery.data,
    bases.data,
    groupList.data,
    canSeeActivity,
    canSeeGovernance,
    canSeePeople,
    fmt,
    t
  ])

  const results = useMemo(() => rankPaletteItems(items, query), [items, query])
  const groups = useMemo(() => groupPaletteItems(results), [results])

  const loading = conversations.isLoading || gallery.isLoading || bases.isLoading || groupList.isLoading

  const go = (item: PaletteItem | undefined) => {
    if (!item) return
    onClose()
    navigate(item.to)
  }

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActive((index) => (results.length === 0 ? 0 : (index + 1) % results.length))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((index) => (results.length === 0 ? 0 : (index - 1 + results.length) % results.length))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      go(results[active])
    }
  }

  const activeId = results[active]?.id

  return (
    // The backdrop is inert. Dismissing is Escape or the close key — a stray click while
    // reading the list must not throw away what was typed.
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[var(--scrim)]/35 p-4 pt-[12vh] backdrop-blur-[2px]">
      {/*
        The dialog owns the arrow keys for the listbox it labels, which is why the handler is
        here rather than on the input: Escape has to reach the trap and Enter has to commit
        whichever option the arrows landed on, wherever focus happens to be inside the panel.
      */}
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={t.palette.title}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        className="w-full max-w-[560px] overflow-clip rounded-shell border border-rule bg-card-solid shadow-frame focus:outline-none"
      >
        <div className="flex items-center gap-2.5 border-b border-rule px-4">
          <Search size={14} className="shrink-0 text-fg-subtle" aria-hidden />
          <input
            ref={input}
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value)
              setActive(0)
            }}
            placeholder={t.palette.placeholder}
            aria-label={t.palette.placeholder}
            aria-controls="command-palette-results"
            aria-activedescendant={activeId}
            autoComplete="off"
            spellCheck={false}
            className="h-[52px] min-w-0 flex-1 bg-transparent text-[15px] tracking-[-0.008em] text-fg placeholder:text-fg-subtle focus:outline-none"
          />
          {loading && <Spinner className="text-fg-subtle" />}
          <kbd
            aria-hidden
            className="hidden shrink-0 rounded-md border border-rule px-1.5 py-[1px] font-mono text-[9.5px] text-fg-subtle sm:block"
          >
            esc
          </kbd>
          {/* The backdrop is inert by design, so this is the only way out that is not a key
              press — and a modal with no visible exit is a trap for anyone on a touch screen. */}
          <IconButton label={t.palette.close} size={26} onClick={onClose}>
            <X size={13} aria-hidden />
          </IconButton>
        </div>

        <div
          id="command-palette-results"
          role="listbox"
          aria-label={t.palette.results}
          className="max-h-[52vh] overflow-y-auto p-2"
        >
          {results.length === 0 ? (
            <p className="px-3 py-6 text-center text-[13px] text-fg-subtle">
              {loading ? t.palette.looking : t.palette.noMatches}
            </p>
          ) : (
            groups.map((group) => (
              <div key={group.groupId} className="mb-1 last:mb-0">
                <div className="flex items-center gap-2 px-2.5 pb-1 pt-2">
                  <span className="eyebrow">{t.palette.groups[group.groupId]}</span>
                  <span className="h-px flex-1 bg-rule" />
                </div>
                {group.items.map((item) => {
                  const index = results.indexOf(item)
                  const selected = index === active
                  return (
                    <button
                      key={item.id}
                      id={item.id}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      tabIndex={-1}
                      onMouseMove={() => setActive(index)}
                      onClick={() => go(item)}
                      className={clsx(
                        'flex w-full items-center gap-2.5 rounded-chip px-2.5 py-2 text-left transition-colors',
                        selected ? 'bg-primary-soft' : 'hover:bg-card'
                      )}
                    >
                      <span className={clsx('shrink-0', selected ? 'text-primary' : 'text-fg-subtle')}>
                        {GROUP_ICONS[item.groupId] ?? DESTINATION_ICONS[item.to] ?? <Search size={13} aria-hidden />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium tracking-[-0.012em] text-fg">
                          {item.title}
                        </span>
                        {item.detail && (
                          <span className="block truncate text-[11.5px] text-fg-subtle">{item.detail}</span>
                        )}
                      </span>
                      {selected && <CornerDownLeft size={12} className="shrink-0 text-fg-subtle" aria-hidden />}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
